/**
 * LAB ADMIN (phase B+C) — the lab-owner ERP cockpit FeatureModule. Six surfaces under /admin/*:
 *
 *   /admin                 — Tableau de bord (overview KPIs)            [any admin cap → partial board]
 *   /admin/profil          — Profil du laboratoire (view/edit)          [lab_admin.profil.read]
 *   /admin/equipe          — Équipe & permissions (staff + grants)      [iam.utilisateur.manage]
 *   /admin/journal         — Journal d'audit (filtered, paginated)      [audit.journal.read_all]
 *   /admin/tarification    — Tarification des analyses (M7 ledger)      [catalogue.parametre.price]
 *   /admin/abonnement      — Abonnement (entitlement + change-request)  [lab_admin.abonnement.read]
 *
 * Gating model — IDENTICAL to the finance/inventory pattern. The six screens need DIFFERENT
 * capabilities and the router applies ONE module-level gate per route (router.tsx). So we keep the
 * MODULE gate EMPTY (any authenticated user reaches the shell route) and gate two ways:
 *   1. each NAV item carries its own requiredCapabilities → the side-nav shows only what you may open;
 *   2. each SCREEN renders its own <ForbiddenState> when its read/entry capability is missing, and every
 *      mutating action is gated by its exact server capability via useCan.
 * The server re-checks every request — useCan only hides/disables UI (never the trust boundary), live
 * per request (deny-wins, never frozen in the JWT).
 *
 * The overview (/admin) carries NO nav-item cap (it lands on the first cap held; partial rights = partial
 * board) — but its route still reaches the shell for any authed user, and it ForbiddenStates when the
 * user holds NONE of the admin caps. The section sorts to the bottom (order band 95-99), after the
 * operational sections.
 *
 * Capabilities (exact server keys; the NET-NEW lab_admin.* are declared in the lab-admin module manifest,
 * the rest are the REUSED owner-scoped trio owned by iam/catalogue/audit):
 *   lab_admin.profil.read|manage · lab_admin.abonnement.read|request
 *   iam.utilisateur.manage · audit.journal.read_all · catalogue.parametre.price
 */
import type { FeatureModule } from "@/app";
import { ADMIN_CAPS } from "./caps";
import { registerAdminMessages } from "./messages";
import { OverviewPage } from "./screens/OverviewPage";
import { ProfilePage } from "./screens/ProfilePage";
import { TeamPage } from "./screens/TeamPage";
import { JournalPage } from "./screens/JournalPage";
import { PricingPage } from "./screens/PricingPage";
import { SubscriptionPage } from "./screens/SubscriptionPage";

const SECTION = "nav.section.administration";

const adminModule: FeatureModule = {
  id: "admin",
  title: "Administration",
  // Empty module gate (see header): per-screen ForbiddenState + per-nav-item caps do the gating,
  // because the screens require different capabilities and the router applies ONE module gate.
  requiredCapabilities: [],
  routes: [
    { path: "/admin", element: <OverviewPage /> },
    { path: "/admin/profil", element: <ProfilePage /> },
    { path: "/admin/equipe", element: <TeamPage /> },
    { path: "/admin/journal", element: <JournalPage /> },
    { path: "/admin/tarification", element: <PricingPage /> },
    { path: "/admin/abonnement", element: <SubscriptionPage /> },
  ],
  nav: [
    {
      id: "admin-overview",
      path: "/admin",
      label: "nav.admin.overview",
      icon: "▤",
      order: 95,
      section: SECTION,
      // No cap: the overview self-gates (partial board on partial rights; ForbiddenState on none).
    },
    {
      id: "admin-profile",
      path: "/admin/profil",
      label: "nav.admin.profile",
      icon: "⬡",
      order: 96,
      section: SECTION,
      requiredCapabilities: [ADMIN_CAPS.profilRead],
    },
    {
      id: "admin-team",
      path: "/admin/equipe",
      label: "nav.admin.team",
      icon: "◉",
      order: 97,
      section: SECTION,
      requiredCapabilities: [ADMIN_CAPS.staffManage],
    },
    {
      id: "admin-journal",
      path: "/admin/journal",
      label: "nav.admin.journal",
      icon: "≣",
      order: 98,
      section: SECTION,
      requiredCapabilities: [ADMIN_CAPS.journalReadAll],
    },
    {
      id: "admin-pricing",
      path: "/admin/tarification",
      label: "nav.admin.pricing",
      icon: "₪",
      order: 99,
      section: SECTION,
      requiredCapabilities: [ADMIN_CAPS.pricing],
    },
    {
      id: "admin-subscription",
      path: "/admin/abonnement",
      label: "nav.admin.subscription",
      icon: "◆",
      order: 100,
      section: SECTION,
      requiredCapabilities: [ADMIN_CAPS.abonnementRead],
    },
  ],
  onRegister() {
    registerAdminMessages();
  },
};

export default adminModule;
